import { PlusOutlined } from '@ant-design/icons';
import { ValidatorProvider } from '@dofleini/validator';
import { Button, Form } from 'antd';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDialogsContext } from '@/contexts/DialogsContext';
import PartnerStateProposal
  from '@/modules/proposalRequest/components/ProposalCreateForm/ProposalVerifyData/components/PartnerStateProposal';
import ProposalRequestPartnerFormModal from '@/modules/proposalRequest/containers/ProposalRequestPartnerFormModal';
import ProposalRequestStatePartnerFormModal
  from '@/modules/proposalRequest/containers/ProposalRequestStatePartnerFormModal';
import {
  DIALOG_PARTNER_NAMESPACE,
  DIALOG_STATE_PARTNER_NAMESPACE
} from '@/modules/proposalRequest/contants/proposalRequest';

const ProposalStatePartners = ({
  value,
  onChange,
  addLabelText,
  proposal,
  cashEdit = true,
  ...props
}) => {
  const { openDialog } = useDialogsContext(DIALOG_STATE_PARTNER_NAMESPACE);
  const { openDialog: openDialogRepresentative } = useDialogsContext(DIALOG_PARTNER_NAMESPACE);
  const { t } = useTranslation('proposalRequest');
  let [data, setData] = useState([]);

  useEffect(() => {
    if (value) {
      setData(value);
    }
  }, [value]);

  const onAddRequest = useCallback(() => {
    openDialog();
  }, [openDialog]);

  const onAddPartner = useCallback(async (partner, isEdit, recordIndex) => {
    return new Promise((resolve, reject) => {
      try {
        if (!isEdit) {
          data.push(partner);
        } else {
          data[recordIndex] = partner;
        }
        setData([...data]);
        onChange([...data]);
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });
  }, [data, onChange]);

  const onEditPartner = useCallback(async (record, i) => {
    openDialog({ partner: { ...record }, isEdit: true, recordIndex: i });
  }, [openDialog]);

  const onDeletePartner = useCallback(async (index) => {
    data.splice(index, 1);
    setData([...data]);
    onChange([...data]);
  }, [data, onChange]);

  const onEditRepresentative = useCallback(async (record, idx) => {
    openDialogRepresentative({ partner: { ...record }, isEdit: true, recordIndex: idx });
  }, [openDialogRepresentative]);

  const onAddRepresentative = useCallback((record, isEdit, i) => {
    data[i].representative = record;
    onChange([...data]);
    setData(data);
  }, [data, onChange]);

  const onDeleteRepresentative = useCallback((i) => {
    data[i].representative = undefined;
    onChange([...data]);
    setData(data);
  }, [data, onChange]);

  const onChangePartnerContribution = useCallback(async (record, index) => {
    data[index] = record;
    setData([...data]);
    onChange([...data]);
  }, [data, onChange]);

  return (
    <div className={classNames({ 'mt-4': !isEmpty(data) })} {...props}>
      <ProposalRequestStatePartnerFormModal onConfirm={onAddPartner}/>
      <ProposalRequestPartnerFormModal
        proposal={proposal}
        onConfirm={onAddRepresentative}
        titleText={t('proposalRequest.form.partners.addRepresentativePartners')}
        isStatePartner={true}
        descriptionText={t('proposalRequest.form.partners.addRepresentativePartners')}/>
      <div>
        {data?.map((e, i) =>
          <>
            <PartnerStateProposal
              data={e}
              key={i}
              index={i}
              proposal={proposal}
              onEditPartner={() => onEditPartner(e, i)}
              onDeletePartner={() => onDeletePartner(i)}
              onEditRepresentative={() => onEditRepresentative(e?.representative, i)}
              onDeleteRepresentative={() => onDeleteRepresentative(i)}
              onAddRepresentative={() => openDialogRepresentative({ isEdit: false, recordIndex: i })}
              addLabelText={'proposalRequest.form.partners.addRepresentativePartners'}
              onChange={(y, index) => onChangePartnerContribution(y, index)}
              cashEdit={cashEdit}
              isLast={i === data?.length - 1}
            />
          </>)}
      </div>
      {!cashEdit ? null : <Button
        type={'primary'}
        onClick={onAddRequest}
        className={'cursor-pointer my-2 bg-secondary border-secondary'}
        size={'large'} icon={<PlusOutlined/>}
      >
        {t(addLabelText || 'proposalRequest.form.partners.addText')}
      </Button>}
    </div>
  );

};

ProposalStatePartners.propTypes = {
  addLabelText: PropTypes.string,
  value: PropTypes.array,
  proposal: PropTypes.object,
  setProposal: PropTypes.func,
  onChange: PropTypes.func,
  cashEdit: PropTypes.bool
};

export default memo(ProposalStatePartners);

export const renderProposalStatePartners = ({ formItemProps, field, form }) => {
  const rules = field?.required && [{ required: true, message: ValidatorProvider.t('validateMessages.required') }];

  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <ProposalStatePartners {...field?.widgetProps} {...field} form={form}/>
    </Form.Item>
  );
};
